import React, { useState, useEffect } from 'react'
import { useApiQuery } from '../../../utils/api'
import { Loading, Error } from '../../Elements/Indicators'
import { Header1 } from '../../Elements/Type'
import { UsersIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'
import { RadioGroup } from '@headlessui/react'
import { PageHeader } from '../../Elements/PageHeader'
import { Outlet, useLocation } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { XProject } from '@jirax/common'

interface BoardData {
  projectName: string;
  projectKey: string;
  projectId: number;
  boardId: number;
  boardName: string;
}

interface ProjectData extends XProject {
  boards: {
    id: number;
    name: string;
  }[];
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const [board, setBoard] = useState<BoardData | null>(null)
  const {
    isLoading,
    error,
    data: projects
  } = useApiQuery<ProjectData[]>(['projects'])

  useEffect(() => {
    // nothing to do while projects are still undefined / loading
    if (!projects || projects.length === 0) return

    // If a board has already been selected (user switched boards), do not run
    if (board !== null) return

    // sort **copy** to avoid mutating react-query cache
    const sorted = [...projects].sort((a, b) => (a.id > b.id ? -1 : 1))
    const boards = getBoards(sorted)

    // no boards – stay on empty state
    if (boards.length === 0) {
      setBoard(null)
      return
    }

    const lastBoard = boards[boards.length - 1]
    setBoard(lastBoard)
    navigate(`/dashboard/${lastBoard.boardId}`)
  }, [projects, board, navigate])

  useEffect(() => {
    if (
      location.pathname === '/dashboard' &&
      board &&
      board.boardId !== undefined
    ) {
      navigate(`/dashboard/${board.boardId}`)
    }
    // include `board.boardId` so react warns if it changes without board ref changing
  }, [location, board, navigate])

  const getBoards = (projects: ProjectData[]): BoardData[] => {
    return projects.flatMap(project =>
      project.boards.map(board => ({
        projectName: project.name,
        projectKey: project.key,
        projectId: Number(project.id), // Convert to number to fix type mismatch
        boardId: board.id,
        boardName: board.name
      }))
    ).sort((a, b) =>
      a.projectName.localeCompare(b.projectName) ||
      a.boardName.localeCompare(b.boardName)
    )
  }

  const changeBoard = (boardId: number): void => {
    const boards = getBoards(projects || [])
    const board = boards.find(board => board.boardId === boardId)
    if (board) {
      setBoard(board)
      navigate(`/dashboard/${board.boardId}`)
    }
  }

  if (isLoading) {
    return <Loading message={'Loading projects.'} />
  }

  if (error) {
    return <Error message={(error as Error).message} />
  }

  // If we have *some* projects but no boards we consider this an empty state.
  if (projects && projects.length && board) {
    return (
      <>
        <PageHeader config={{ pageTitle: 'Dashboard' }} />
        <main>
          <div className='mx-auto max-w-7xl sm:px-6 lg:px-8'>
            <RadioGroup
              value={board.boardId}
              onChange={changeBoard}
              className={clsx(
                'grid grid-cols-1 gap-2 px-4 sm:gap-8 sm:px-0',
                'sm:grid-cols-' + getBoards(projects).length
              )}
            >
              {getBoards(projects).map(board => (
                <RadioGroup.Option
                  key={`${board.projectKey}-${board.boardId}`}
                  value={board.boardId}
                  className={({ checked }) =>
                    clsx(
                      checked && 'bg-sky-900 bg-opacity-75 text-white',
                      // base card look with subtle shadow
                      'relative col-span-1 flex cursor-pointer rounded-lg px-5 py-2 shadow-md',
                      // hover & transition states
                      'transition-all duration-150 hover:shadow-lg hover:ring-2 hover:ring-indigo-500/50',
                      // stronger shadow on focus/active (replacing blue outline)
                      'focus:outline-none focus-visible:shadow-lg active:shadow-lg'
                    )
                  }
                >
                  {({ checked }) => (
                    <>
                      <div className='flex w-full items-center justify-between'>
                        <div className='mr-4 shrink-0 text-white'>
                          {checked ? (
                            <UsersIcon className='text-white-700 h-6 w-6' />
                          ) : (
                            <UsersIcon className='h-6 w-6 text-slate-500' />
                          )}
                        </div>

                        <div className='flex grow items-center'>
                          <div className='text-sm'>
                            <RadioGroup.Label
                              as='p'
                              className={`font-medium  ${checked ? 'text-white' : 'text-gray-900'
                                }`}
                            >
                              {board.projectName}
                            </RadioGroup.Label>
                            <RadioGroup.Description
                              as='span'
                              className={`inline ${checked ? 'text-sky-100' : 'text-gray-500'
                                }`}
                            >
                              <span>{board.boardName}</span>
                            </RadioGroup.Description>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </RadioGroup.Option>
              ))}
            </RadioGroup>
          </div>
          <div className='mx-auto max-w-7xl sm:px-6 lg:px-8'>
            <div className='px-4 py-8 sm:px-0'>
              <Outlet />
            </div>
          </div>
        </main>
      </>
    )
  }

  /* Either no projects at all, or projects array is empty, or no boards found */
  return <Header1 title="Empty State" />
}

export default Dashboard
